import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { shouldUseMockData } from '@/config/api.resolver';
import { mockCoursesData } from './mock/courses.mock';
import type { CourseDetailsResponse, ModulesDetailedResponse } from '@/types/userCourseProgress.types';

// Define interfaces for type safety
interface CourseFilters {
  page?: number;
  limit?: number;
  search?: string;
  title?: string;
  category?: string;
  difficulty?: string;
  minModules?: number;
  maxModules?: number;
  sortBy?: string;
  sortOrder?: string;
  isPublished?: boolean;
  hasVideo?: boolean;
  hasAudio?: boolean;
  status?: string;
  course_ids?: string[];
}

/** One row on the course Completion Config tab. Mirrors the SDK DTO. */
export interface CourseAssessmentConfigItem {
  assessment_id: string;
  title: string;
  assessment_level: 'COURSE' | 'MODULE' | 'CONTENT_ITEM';
  assessment_type: 'PRE' | 'POST';
  parent_name?: string;
  module_id?: string;
  content_item_id?: string;
  module_order: number;
  content_item_order: number;
  total_questions: number;
  time_limit_minutes?: number;
  /** True when the learner must pass this assessment for the course to complete. */
  required_for_completion: boolean;
}

/** One completion requirement resolved against the current learner. */
export interface CourseCompletionRequirement {
  assessment_id: string;
  title: string;
  assessment_level: 'COURSE' | 'MODULE' | 'CONTENT_ITEM';
  assessment_type: 'PRE' | 'POST';
  passed: boolean;
}

export interface CourseCompletionRequirementStatus {
  course_id: string;
  requirements: CourseCompletionRequirement[];
  /** True when there is nothing left to pass (or the course has no requirements). */
  all_passed: boolean;
  pending_count: number;
}

/* ----------------------- Custom completion config ------------------------ */

export type CompletionConditionType = 'terms_and_conditions';

/** Terms & Conditions payload on a completion condition. */
export interface TermsAndConditions {
  title: string;
  source: 'pdf' | 'inline';
  document_url?: string;   // presigned PDF url (source=pdf)
  file_name?: string;
  mime_type?: string;
  html_content?: string;   // inline HTML (source=inline)
  version: number;
  updated_at?: string;
}

/** One custom completion condition on a course. */
export interface CompletionCondition {
  id: string;
  type: CompletionConditionType;
  is_enabled: boolean;
  terms_and_conditions?: TermsAndConditions;
}

/** The whole custom completion config for a course (admin view). */
export interface CourseCompletionConfig {
  course_id: string;
  custom_enabled: boolean;
  /** Whether learners may tick a lesson complete by hand. */
  manual_completion_enabled: boolean;
  conditions: CompletionCondition[];
}

/** One condition resolved against the current learner. */
export interface CompletionConditionStatus {
  id: string;
  type: CompletionConditionType;
  is_satisfied: boolean;
  title?: string;
  source?: 'pdf' | 'inline';
  document_url?: string;
  html_content?: string;
  version?: number;
  accepted_at?: string;
}

/** Aggregated completion gate for the current learner (assessments + conditions). */
export interface CourseCompletionGateStatus {
  course_id: string;
  all_satisfied: boolean;
  assessment_requirements: CourseCompletionRequirement[];
  conditions: CompletionConditionStatus[];
  pending_count: number;
  /** False for SCORM courses regardless of the configured value. */
  manual_completion_enabled?: boolean;
}

export interface AcceptTermsResult {
  course_id: string;
  condition_id: string;
  accepted: boolean;
  all_satisfied: boolean;
  course_completed: boolean;
}

/** One T&C condition and whether/when a given learner accepted it (admin view). */
export interface TermsAcceptanceRecord {
  condition_id: string;
  title: string;
  source?: 'pdf' | 'inline';
  version: number;
  accepted: boolean;
  accepted_at?: string;
  accepted_version?: number;
  ip_address?: string;
  user_agent?: string;
}

export interface UserTermsAcceptanceStatus {
  course_id: string;
  user_id: string;
  records: TermsAcceptanceRecord[];
}

// Minimal course list filters
interface MinimalCourseFilters {
  page?: number;
  limit?: number;
  stage?:'generating' | 'draft' | 'published' | 'unpublished';
  difficulty?: string;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'rating' | 'users_assigned';
  sortOrder?: 'asc' | 'desc';
  source_type?: string;   // 'ai' | 'manual' | 'scorm_imported' — how the course was created
  // Content format: 'html' | 'html_with_audio' | 'video_presentation' | 'avatar_video'.
  // Orthogonal to source_type; the catalog sends one or the other, never both.
  content_delivery_type?: string;
}

// Minimal course interface
interface MinimalCourse {
  _id: string;
  title: string;
  description: string;
  stage: 'generating' |'draft' | 'published' | 'unpublished';
  difficulty: string;
  module_count: number;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  createdAt: string;
  updatedAt: string;
  users_assigned: number;
  rating: number;
  estimated_duration?: {
    total: {
      hours: number;
      minutes: number;
    };
  };
  source_type?: string;
  content_delivery_type?: string;
}

// Minimal course list response
interface MinimalCourseListResponse {
  success: boolean;
  description: string;
  data: {
    courses: MinimalCourse[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    total: number;
    published_course_count: number;
    unpublished_course_count: number;
    draft_course_count: number;
  };
}

// Course Stage enum
type CourseStage = 'draft' | 'published' | 'unpublished';

// Course Stage Update Request
interface CourseStageUpdateRequest {
  stage: CourseStage;
}

// Course Stage Update Response
interface CourseStageUpdateResponse {
  success: boolean;
  description: string;
  data: {
    course_id: string;
    stage: CourseStage;
    message: string;
    updated_at: string;
  };
}

interface CourseData {
  // Define the structure of your course data
  [key: string]: any;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  description?: string;
  /** Some endpoints return `message` instead of `description`. */
  message?: string;
  meta_data?: {
    total_records: number;
    current_page: number;
    records_per_page: number;
  };
}

class CourseService {
  private useMockData: boolean;

  constructor() {
    // Use dynamic resolver to determine if mock data should be used
    this.useMockData = shouldUseMockData();
  }

  /**
   * Per-tenant course capabilities (e.g. whether avatar_video delivery is enabled).
   * Backend resolves this from tenant config — replaces the build-time
   * VITE_AVATAR_VIDEO_DOMAINS gate. Defaults to disabled on any error.
   */
  async getCapabilities(): Promise<{ avatar_video_enabled: boolean }> {
    try {
      const response = await apiClient.get<any>('/course/capabilities');
      const body = response.data?.data ?? response.data;
      return { avatar_video_enabled: !!body?.avatar_video_enabled };
    } catch (error) {
      console.error('Failed to fetch course capabilities:', error);
      return { avatar_video_enabled: false };
    }
  }

  async getCourses(filters: CourseFilters = {}) {

    if (this.useMockData) {
      return this.getMockCourses(filters);
    }

    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const url = `${API_ENDPOINTS.COURSES.LIST}?${params}`;

      const response = await apiClient.get<ApiResponse<any[]>>(url);

      if (response.data.success) {
        const transformedCourses = response.data.data.map(course => this.transformApiCourse(course));

        return {
          data: transformedCourses,
          total: response.data.meta_data?.total_records,
          page: response.data.meta_data?.current_page,
          pageSize: response.data.meta_data?.records_per_page,
        };
      }

      throw new Error(response.data.description || 'Failed to fetch courses');
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  }

  /**
   * Get minimal course list - optimized for efficient listing
   */
  async getMinimalCourses(filters: MinimalCourseFilters = {}) {

    if (this.useMockData) {
      return this.getMockMinimalCourses(filters);
    }

    try {
      const params = new URLSearchParams();

      // Build query parameters
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.difficulty) params.append('difficulty', filters.difficulty);
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.source_type) params.append('source_type', filters.source_type);
      if (filters.content_delivery_type) params.append('content_delivery_type', filters.content_delivery_type);

      const url = `${API_ENDPOINTS.COURSES.LIST_MINIMAL}?${params}`;

      const response = await apiClient.get<MinimalCourseListResponse>(url);

      if (response.data.success) {
        return {
          courses: response.data.data.courses,
          pagination: response.data.data.pagination,
          total: response.data.data.total,
          publishedCourseCount: response.data.data.published_course_count,
          unpublishedCourseCount: response.data.data.unpublished_course_count,
          draftCourseCount: response.data.data.draft_course_count
        };
      }

      throw new Error(response.data.description || 'Failed to fetch minimal courses');
    } catch (error) {
      console.error('Error fetching minimal courses:', error);
      throw error;
    }
  }

  /** Load one course. `id` is ALWAYS the master course id. */
  async getCourseById(id: string, languageCode?: string) {
    if (this.useMockData) {
      return this.getMockCourseById(id);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.GET_DETAILS(id, languageCode)
      );
      
      if (response.data.success) {
        const transformedData = this.transformApiCourseDetails(response.data.data);
        return {
          ...response,
          data: {
            ...response.data,
            data: transformedData
          }
        };
      }
      throw new Error(response.data.description || 'Failed to fetch course');
    } catch (error) {
      console.error('Error fetching course:', error);
      throw error;
    }
  }

  async getCourseDetails(id: string) {
    if (this.useMockData) {
      return this.getMockCourseDetails(id);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.GET_DETAILS(id));

      if (response.data.success) {
        return this.transformApiCourseDetails(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to fetch course details');
    } catch (error) {
      console.error('Error fetching course details:', error);
      throw error;
    }
  }

  /**
   * Get complete course data in a single API call
   * Includes course details, all modules, all lessons, all assessments with media URLs
   */
  /**
   * The learner player's single read — the whole course in one call. `courseId`
   * is ALWAYS the master's; `languageCode` picks which language version comes
   * back.
   */
  async getCompleteCourseData(courseId: string, languageCode?: string) {
    if (this.useMockData) {
      return this.getMockCourseDetails(courseId);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.GET_COMPLETE(courseId, languageCode)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || response.data.description || 'Failed to fetch complete course data');
    } catch (error) {
      console.error('Error fetching complete course data:', error);
      throw error;
    }
  }

  /**
   * `moduleId` is always the MASTER module id; `languageCode` picks which
   * language's lessons come back. See getCourseById for why identity does not
   * change with language.
   */
  async getModuleContentTree(moduleId: string, languageCode?: string) {
    if (this.useMockData) {
      return this.getMockModuleContentTree(moduleId);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.MODULE_CONTENT_TREE(moduleId, languageCode)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch module content');
    } catch (error) {
      console.error('Error fetching module content:', error);
      throw error;
    }
  }

  async createCourse(courseData: CourseData) {
    if (this.useMockData) {
      return this.createMockCourse(courseData);
    }

    try {
      const apiData = this.transformToApiFormat(courseData);
      const response = await apiClient.post<ApiResponse<any>>(API_ENDPOINTS.COURSES.CREATE, apiData);

      if (response.data.success) {
        return this.transformApiCourse(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to create course');
    } catch (error) {
      console.error('Error creating course:', error);
      throw error;
    }
  }

  /**
   * Manually create an empty course skeleton (no AI generation). Returns the
   * new course id so the caller can navigate to the Edit Course flow.
   */
  async createManualCourse(dto: {
    title: string;
    description: string;
    target_audience?: string;
    difficulty_level?: string;
    prerequisites?: string[];
    industry?: string;
    language_code?: string;
    content_delivery_type?: 'html' | 'html_with_audio' | 'video_presentation';
    theme?: string;
    estimated_duration?: { hours?: number; minutes?: number };
    // Voice used whenever audio is generated for this course. Only sent for
    // html_with_audio / video_presentation — html never produces audio.
    tts_config?: {
      voice_name?: string;
      voice_type?: string;
      speaking_rate?: number;
      pitch?: number;
      volume_gain_db?: number;
    };
    // Captions are burned into rendered scene videos, so only sent for
    // video_presentation.
    caption_config?: { enable_captions: boolean };
  }): Promise<{ course_id: string; orchestration_id?: string; title?: string; stage?: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSES.CREATE_MANUAL, dto);

      if (response.data.success && response.data.data) {
        const outer = response.data.data;
        // Controller wraps the SDK result: { success, message, data: {...} }
        const inner = outer.data && (outer.data.course_id || outer.data._id) ? outer.data : outer;
        const course_id = inner.course_id || inner._id;
        if (course_id) {
          return {
            course_id,
            orchestration_id: inner.orchestration_id,
            title: inner.title,
            stage: inner.stage,
          };
        }
      }
      throw new Error(response.data.description || 'Failed to create course');
    } catch (error: any) {
      console.error('Error creating manual course:', error);
      const data = error?.response?.data;
      const serverMessage =
        data?.error?.error_description ||
        data?.description ||
        data?.error?.message ||
        data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      throw error;
    }
  }

  async updateCourse(id: string, courseData: CourseData) {
    if (this.useMockData) {
      return this.updateMockCourse(id, courseData);
    }

    try {
      const apiData = this.transformToApiFormat(courseData);
      const response = await apiClient.put<ApiResponse<any>>(API_ENDPOINTS.COURSES.UPDATE(id), apiData);

      if (response.data.success) {
        return this.transformApiCourse(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to update course');
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  }

  async updateCourseMetadata(courseId: string, metadata: any) {
    if (this.useMockData) {
      return this.updateMockCourse(courseId, metadata);
    }

    try {
      const requestBody = {
        title: metadata.title,
        description: metadata.description,
        slug: metadata.slug,
        learning_objectives: metadata.learning_objectives || [],
        prerequisites: metadata.prerequisites || [],
        target_audience: metadata.target_audience || [],
        tags: metadata.tags || [],
        difficulty: metadata.difficulty?.toUpperCase() || 'INTERMEDIATE',
        category_id: metadata.category_id,
        category_name: metadata.category_name,
        language: metadata.language || 'ENGLISH',
        language_code: metadata.language_code,
        resources: metadata.resources || [],
        final_project: metadata.final_project || {},
        assessment_overview: metadata.assessment_overview || {},
        next_steps: metadata.next_steps || {},
        estimated_duration: metadata.estimated_duration || {
          total: { hours: 0, minutes: 0 },
          videos: { hours: 0, minutes: 0 },
          practice: { hours: 0, minutes: 0 },
          assessments: { hours: 0, minutes: 0 }
        },
        thumbnail_image_url: metadata.thumbnail_image_url,
        banner_image_url: metadata.banner_image_url,
        // Only when the form changed it ('' = back to the default template)
        ...(metadata.certificate_template_id !== undefined ? { certificate_template_id: metadata.certificate_template_id } : {}),
      };

      const response = await apiClient.patch<ApiResponse<any>>(API_ENDPOINTS.COURSES.UPDATE_METADATA(courseId), requestBody);

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update course metadata');
    } catch (error) {
      console.error('Error updating course metadata:', error);
      throw error;
    }
  }

  async deleteCourse(id: string) {
    if (this.useMockData) {
      return this.deleteMockCourse(id);
    }

    try {
      const response = await apiClient.delete<ApiResponse<any>>(API_ENDPOINTS.COURSES.DELETE(id));

      if (response.data.success) {
        if (response?.data?.meta_data) {
          return response?.data;
        } else {
          return { success: true, message: 'Course deleted successfully' };
        }
      }
      throw new Error(response.data.description || 'Failed to delete course');
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  }

  async duplicateCourse(id: string) {
    if (this.useMockData) {
      return this.duplicateMockCourse(id);
    }

    try {
      const response = await apiClient.post<ApiResponse<any>>(API_ENDPOINTS.COURSES.DUPLICATE(id));

      if (response.data.success) {
        return this.transformApiCourse(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to duplicate course');
    } catch (error) {
      console.error('Error duplicating course:', error);
      throw error;
    }
  }

  async getCourseModules(courseId: string) {
    if (this.useMockData) {
      return mockCoursesData.courseModules[courseId] || [];
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.GET_MODULES(courseId));

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch modules');
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  }

  /**
   * Create a module manually under a course.
   * `order` is the 0-based position where the module should be inserted;
   * the backend shifts subsequent modules down.
   */
  async createModule(
    courseId: string,
    data: {
      title: string;
      description?: string;
      estimated_duration?: { hours: number; minutes: number };
      learning_objectives?: { description: string; bloom_level: string }[];
      order?: number;
    }
  ) {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.CREATE_MODULE(courseId),
        data
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create module');
    } catch (error) {
      console.error('Error creating module:', error);
      throw error;
    }
  }

  /**
   * Update an existing module's editable fields.
   */
  async updateModule(
    moduleId: string,
    data: {
      title?: string;
      description?: string;
      estimated_duration?: { hours: number; minutes: number };
      learning_objectives?: { description: string; bloom_level: string }[];
    }
  ) {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.UPDATE_MODULE(moduleId),
        data
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update module');
    } catch (error) {
      console.error('Error updating module:', error);
      throw error;
    }
  }

  /**
   * Delete a module (and its content) from a course.
   */
  async deleteModule(moduleId: string) {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.DELETE_MODULE(moduleId)
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete module');
    } catch (error) {
      console.error('Error deleting module:', error);
      throw error;
    }
  }

  /**
   * Persist a new module ordering. `moduleIds` is the full list of module
   * ids in the desired display order.
   */
  async reorderModules(courseId: string, moduleIds: string[]) {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.REORDER_MODULES(courseId),
        { module_ids: moduleIds }
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to reorder modules');
    } catch (error) {
      console.error('Error reordering modules:', error);
      throw error;
    }
  }

  /**
   * Create a content item under a module. `type` is one of html |
   * html_with_audio | video_presentation | avatar_video | scorm. `order` is an
   * optional 0-based insert position (defaults to the end).
   */
  async createContentItem(
    moduleId: string,
    data: { title: string; type?: string; order?: number }
  ) {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.CREATE_CONTENT_ITEM(moduleId),
        data
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create content item');
    } catch (error) {
      console.error('Error creating content item:', error);
      throw error;
    }
  }

  /**
   * Delete a content item (and its scenes) from a module.
   */
  async deleteContentItem(contentItemId: string) {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.DELETE_CONTENT_ITEM(contentItemId)
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete content item');
    } catch (error) {
      console.error('Error deleting content item:', error);
      throw error;
    }
  }

  /**
   * Persist a new content-item ordering for a module. `contentItemIds` is the
   * full list of content-item ids in the desired display order.
   */
  async reorderContentItems(moduleId: string, contentItemIds: string[]) {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.REORDER_CONTENT_ITEMS(moduleId),
        { content_item_ids: contentItemIds }
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to reorder content items');
    } catch (error) {
      console.error('Error reordering content items:', error);
      throw error;
    }
  }

  /**
   * Add a scene to a (video/avatar) content item. Optional title/narration/order;
   * defaults to a themed placeholder scene appended at the end.
   */
  async createScene(
    contentItemId: string,
    data?: { title?: string; narration?: string; order?: number }
  ) {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.CREATE_SCENE(contentItemId),
        data || {}
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create scene');
    } catch (error) {
      console.error('Error creating scene:', error);
      throw error;
    }
  }

  /**
   * Persist a new scene ordering for a content item. `sceneIds` is the full list
   * of scene ids in the desired display order.
   */
  async reorderScenes(contentItemId: string, sceneIds: string[]) {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.REORDER_SCENES(contentItemId),
        { scene_ids: sceneIds }
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to reorder scenes');
    } catch (error) {
      console.error('Error reordering scenes:', error);
      throw error;
    }
  }

  /**
   * Delete a scene from a content item.
   */
  async deleteScene(sceneId: string) {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        API_ENDPOINTS.COURSES.DELETE_SCENE(sceneId)
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete scene');
    } catch (error) {
      console.error('Error deleting scene:', error);
      throw error;
    }
  }

  /**
   * Mock-only. There is no LEARNING_PATHS entry in API_ENDPOINTS, so the live
   * branch never had a URL to call — it threw a TypeError. Nothing outside this
   * file calls this; use learningPathService for real learning-path data.
   */
  async getLearningPaths() {
    if (this.useMockData) {
      return mockCoursesData.learningPaths;
    }

    throw new Error('Learning paths endpoint is not configured on courseService — use learningPathService instead');
  }

  async generateCourseWithAI(topic: string, options: any) {
    if (this.useMockData) {
      return mockCoursesData.generateCourseWithAI(topic, options);
    }

    try {
      const response = await apiClient.post<ApiResponse<any>>(API_ENDPOINTS.COURSES.GENERATE, {
        topic,
        ...options,
      });

      if (response.data.success) {
        return this.transformApiCourse(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to generate course');
    } catch (error) {
      console.error('Error generating course:', error);
      throw error;
    }
  }

  async getCourseTracking(courseId: string) {
    if (this.useMockData) {
      return this.getMockCourseTracking(courseId);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.GET_TRACKING(courseId));

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch tracking data');
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      throw error;
    }
  }

  /**
   * Get course details by slug
   */
  async getCourseDetailsBySlug(slug: string) {
    if (this.useMockData) {
      return this.getMockCourseDetails(slug);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.GET_DETAILS_BY_SLUG(slug));

      if (response.data.success) {
        return this.transformApiCourseDetails(response.data.data);
      }
      throw new Error(response.data.description || 'Failed to fetch course details');
    } catch (error) {
      console.error('Error fetching course details by slug:', error);
      throw error;
    }
  }

  /**
   * Get content item with scenes
   */
  async getContentItemScenes(contentItemId: string) {
    if (this.useMockData) {
      return this.getMockContentItemScenes(contentItemId);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.CONTENT_ITEM_SCENES(contentItemId));

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch content item scenes');
    } catch (error) {
      console.error('Error fetching content item scenes:', error);
      throw error;
    }
  }

  /**
   * Get full course with all modules, content items, and scenes
   */
  async getFullCourse(courseId: string) {
    if (this.useMockData) {
      return this.getMockFullCourse(courseId);
    }

    try {
      const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.COURSES.GET_FULL_COURSE(courseId));

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch full course');
    } catch (error) {
      console.error('Error fetching full course:', error);
      throw error;
    }
  }

  /**
   * Update course stage (draft, published, unpublished)
   */
  async updateCourseStage(courseId: string, stage: CourseStage): Promise<CourseStageUpdateResponse> {
    if (this.useMockData) {
      return this.updateMockCourseStage(courseId, stage);
    }

    try {
      const request: CourseStageUpdateRequest = { stage };
      const response = await apiClient.patch<CourseStageUpdateResponse>(
        API_ENDPOINTS.COURSES.UPDATE_STAGE(courseId), 
        request
      );

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to update course stage');
    } catch (error) {
      console.error('Error updating course stage:', error);
      throw error;
    }
  }

  transformApiCourseDetails(apiCourse: any) {
    return {
      id: apiCourse._id,
      title: apiCourse.title,
      description: apiCourse.description,
      slug: apiCourse.slug,
      category: apiCourse.category_name || 'Uncategorized',
      category_id: apiCourse.category_id,
      category_name: apiCourse.category_name,
      difficulty: apiCourse.difficulty?.toLowerCase() || 'intermediate',
      language: apiCourse.language || 'ENGLISH',
      // No stand-in URL — placeholder services are CSP-blocked, and '' lets the UI show its icon.
      thumbnail: apiCourse.thumbnail_image_url || '',
      banner: apiCourse.banner_image_url || '',
      certificate_template_id: apiCourse.certificate_template_id || '', // '' = default template
      duration: (apiCourse.estimated_duration?.total?.hours || 0) * 60 + (apiCourse.estimated_duration?.total?.minutes || 0),
      estimatedDuration: apiCourse.estimated_duration?.total
        ? `${apiCourse.estimated_duration.total.hours || 0}h ${apiCourse.estimated_duration.total.minutes || 0}m`
        : '0h 0m',
      estimated_duration: apiCourse.estimated_duration,
      modules: apiCourse.modules || [],
      moduleCount: apiCourse.module_count || apiCourse.modules?.length || 0,
      learning_objectives: apiCourse.learning_objectives || [],
      prerequisites: apiCourse.prerequisites || [],
      target_audience: apiCourse.target_audience || [],
      tags: apiCourse.tags || [],
      resources: apiCourse.resources || [],
      final_project: apiCourse.final_project || {
        title: '',
        description: '',
        estimated_time: { hours: 0, minutes: 0 },
        options: [],
        assessment_criteria: [],
        requirements: [],
        deliverables: [],
        grading_criteria: [],
      },
      assessment_overview: apiCourse.assessment_overview || {
        components: [],
        passing_score: 70,
      },
      next_steps: apiCourse.next_steps || {
        topics: [],
        resources: [],
        certificationCriteria: [],
      },
      stage: apiCourse.stage || 'draft',
      status: apiCourse.is_published ? 'published' : 'draft',
      createdAt: apiCourse.created_at,
      updatedAt: apiCourse.updated_at,
      hasVideoContent: apiCourse.has_video_content || false,
      hasAudioContent: apiCourse.has_audio_content || false,
      orchestrationId: apiCourse.orchestration_id,
      // SCORM detection: 'scorm_imported' marks imported SCORM courses. Must be
      // carried through so EditCourse can gate add/delete of modules & content.
      source_type: apiCourse.source_type,
      scorm_id: apiCourse.scorm_id,
      content_delivery_type: apiCourse.content_delivery_type || apiCourse.generation_settings?.content_delivery_type || null,

      // Translation fields. Without these the page cannot tell it is showing a
      // translation, so the read-only guard and language switcher both fail.
      language_code: apiCourse.language_code,
      source_course_id: apiCourse.source_course_id,
      is_translation: apiCourse.is_translation,
      master_language_code: apiCourse.master_language_code,
      generation_settings: apiCourse.generation_settings || null, // Extract generation settings
      pre_assessment: apiCourse.pre_assessment || null, // Course-level pre assessment
      post_assessment: apiCourse.post_assessment || null, // Course-level post assessment
      stats: apiCourse.stats || {
        totalModules: apiCourse.module_count || apiCourse.modules?.length || 0,
        totalContentItems: apiCourse.modules?.reduce((acc: any, m: any) => acc + (m.content_stats?.total || 0), 0) || 0,
      },
      rating: apiCourse.rating || null,
      enrolledUsers: apiCourse.enrolled_users || 0,
      created_by_user: apiCourse.created_by_user,
    };
  }

  transformApiCourse(apiCourse: any) {
    return {
      id: apiCourse._id,
      title: apiCourse.title,
      description: apiCourse.description,
      category: apiCourse.category_name || 'Uncategorized',
      categoryId: apiCourse.category_id,
      difficulty: apiCourse.difficulty || 'Beginner',
      slug: apiCourse.slug,
      thumbnail: apiCourse.thumbnail_image_url || '',
      banner: apiCourse.banner_image_url || '',
      duration: (apiCourse.estimated_duration?.total?.hours || 0) * 60 + (apiCourse.estimated_duration?.total?.minutes || 0),
      modules: apiCourse.module_count || 0,
      chapters: apiCourse.chapter_count || 0,
      status: apiCourse.is_published ? 'published' : 'draft',
      enrolledUsers: apiCourse.enrolled_users || 0,
      completionRate: apiCourse.completion_rate || 0,
      averageScore: apiCourse.average_score || 0,
      rating: apiCourse.rating || 0,
      createdAt: apiCourse.created_at,
      updatedAt: apiCourse.updated_at,
      instructor: apiCourse.instructor || 'System',
      tags: apiCourse.tags || [],
      prerequisites: apiCourse.prerequisites || [],
      targetAudience: apiCourse.target_audience || [],
      enableCertification: apiCourse.enable_certification || false,
      maxEnrollments: apiCourse.max_enrollments || 1000,
      hasVideoContent: apiCourse.has_video_content || false,
      hasAudioContent: apiCourse.has_audio_content || false,
      orchestrationId: apiCourse.orchestration_id,
    };
  }

  transformToApiFormat(courseData: CourseData) {
    const totalMinutes = courseData.duration || 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      title: courseData.title,
      description: courseData.description,
      category_id: courseData.categoryId,
      difficulty: courseData.difficulty,
      estimated_duration: {
        total: { hours, minutes },
        videos: { hours: 0, minutes: 0 },
        practice: { hours: 0, minutes: 0 },
        assessments: { hours: 0, minutes: 0 },
      },
      prerequisites: courseData.prerequisites || [],
      target_audience: courseData.targetAudience || [],
      tags: courseData.tags || [],
      is_published: courseData.status === 'published',
      enable_certification: courseData.enableCertification || false,
      max_enrollments: courseData.maxEnrollments || 1000,
    };
  }

  // Mock data methods
  getMockCourses(filters: CourseFilters): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        let courses = [...mockCoursesData.courses];

        if (filters.status && filters.status !== 'all') {
          courses = courses.filter(c => c.status === filters.status);
        }
        if (filters.category && filters.category !== 'all') {
          courses = courses.filter(c => c.category === filters.category);
        }
        if (filters.difficulty && filters.difficulty !== 'all') {
          courses = courses.filter(c => c.difficulty === filters.difficulty);
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          courses = courses.filter(
            c =>
              c.title.toLowerCase().includes(searchLower) ||
              c.description.toLowerCase().includes(searchLower)
          );
        }

        resolve({
          data: courses,
          total: courses.length,
          page: filters.page || 1,
          pageSize: filters.limit || 10,
        });
      }, 500);
    });
  }

  getMockCourseById(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const course = mockCoursesData.courses.find(c => c.id === id);
        if (course) {
          resolve(course);
        } else {
          reject(new Error('Course not found'));
        }
      }, 500);
    });
  }

  createMockCourse(courseData: CourseData): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        const newCourse = {
          id: Date.now().toString(),
          ...courseData,
          status: 'draft',
          enrolledUsers: 0,
          completionRate: 0,
          averageScore: 0,
          rating: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        // Mock-only path: CourseData doesn't carry every Course field, so the
        // partial mock row is pushed as-is.
        mockCoursesData.courses.push(newCourse as any);
        resolve(newCourse);
      }, 1000);
    });
  }

  updateMockCourse(id: string, courseData: CourseData): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockCoursesData.courses.findIndex(c => c.id === id);
        if (index !== -1) {
          mockCoursesData.courses[index] = {
            ...mockCoursesData.courses[index],
            ...courseData,
            updatedAt: new Date().toISOString(),
          };
          resolve(mockCoursesData.courses[index]);
        } else {
          reject(new Error('Course not found'));
        }
      }, 1000);
    });
  }

  deleteMockCourse(id: string): Promise<{ message: string }> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockCoursesData.courses.findIndex(c => c.id === id);
        if (index !== -1) {
          // Mock Course.status is typed 'published' | 'draft'; soft-delete uses
          // 'archived', which only exists in the mock flow.
          (mockCoursesData.courses[index] as any).status = 'archived';
          resolve({ message: 'Course deleted successfully' });
        } else {
          reject(new Error('Course not found'));
        }
      }, 500);
    });
  }

  duplicateMockCourse(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const course = mockCoursesData.courses.find(c => c.id === id);
        if (course) {
          const duplicatedCourse = {
            ...course,
            id: Date.now().toString(),
            title: `${course.title} (Copy)`,
            status: 'draft' as const,
            enrolledUsers: 0,
            completionRate: 0,
            averageScore: 0,
            rating: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          mockCoursesData.courses.push(duplicatedCourse);
          resolve(duplicatedCourse);
        } else {
          reject(new Error('Course not found'));
        }
      }, 1000);
    });
  }

  getMockCourseDetails(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const course = mockCoursesData.courses.find(c => c.id === id);
        if (course) {
          resolve({
            ...course,
            modules: [
              {
                _id: 'module-1',
                title: 'Module 1: Introduction',
                description: 'Getting started with the basics',
                order: 1,
                learning_objectives: [
                  { id: 'obj-1', description: 'Understand the fundamentals', bloom_level: 'understand' },
                  { id: 'obj-2', description: 'Apply basic concepts', bloom_level: 'apply' },
                ],
                estimated_duration: { hours: 1, minutes: 30 },
                content_stats: { total: 5, video: 2, text: 2, audio: 1 },
              },
            ],
            learningObjectives: [{ id: 'course-obj-1', description: 'Master the complete subject', bloom_level: 'create' }],
            resources: [
              { id: 'res-1', title: 'Documentation', type: 'documentation', url: 'https://docs.example.com' },
              { id: 'res-2', title: 'Video Tutorials', type: 'video', url: 'https://videos.example.com' },
            ],
          });
        } else {
          reject(new Error('Course not found'));
        }
      }, 500);
    });
  }

  getMockModuleContentTree(moduleId: string): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          module: {
            _id: moduleId,
            title: 'Sample Module',
            description: 'This is a sample module with content tree',
            order: 1,
            content_items: [
              {
                _id: 'content-1',
                title: 'Introduction to the Module',
                type: 'video',
                estimated_time: { hours: 0, minutes: 30 },
                scene_count: 3,
                scenes: [
                  {
                    _id: 'scene-1',
                    scene_number: 1,
                    title: 'Welcome Scene',
                    duration: 120,
                    visual_description: 'Opening scene with module overview',
                    media_urls: {
                      s3_video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
                    },
                  },
                ],
              },
            ],
          },
        });
      }, 500);
    });
  }

  getMockCourseTracking(courseId: string): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        const userProgress = Array.from({ length: 20 }, (_, i) => ({
          userId: `user${i + 1}`,
          userName: `User ${i + 1}`,
          avatar: `https://ui-avatars.com/api/?name=User+${i + 1}&background=${['3b82f6', '22c55e', 'f59e0b', 'ef4444', '8b5cf6'][i % 5]}&color=fff`,
          progress: Math.floor(Math.random() * 100),
          modulesCompleted: Math.floor(Math.random() * 6),
          lastAccessed: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          timeSpent: Math.floor(Math.random() * 600),
          score: Math.floor(Math.random() * 40) + 60,
          status: ['active', 'completed', 'dropped'][Math.floor(Math.random() * 3)],
        }));

        resolve({
          courseId,
          enrolledUsers: userProgress.length,
          completionRate: 72,
          averageScore: 85,
          averageTime: 480,
          userProgress,
        });
      }, 500);
    });
  }

  getMockContentItemScenes(contentItemId: string): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          content_item: {
            _id: contentItemId,
            title: 'Sample Content Item',
            content_item_number: 1,
            content_type: 'video',
            estimated_time: 15,
            module: {
              _id: 'module-1',
              title: 'Sample Module',
              module_number: 1
            },
            course: {
              _id: 'course-1',
              title: 'Sample Course',
              slug: 'sample-course'
            }
          },
          scenes: [
            {
              _id: 'scene-1',
              scene_number: 1,
              title: 'Introduction',
              slide_content: 'Welcome to this lesson',
              narration_text: 'In this lesson, we will cover...',
              video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
              media_generation_status: 'completed'
            }
          ],
          navigation: {
            previous_content_item: null,
            next_content_item: {
              _id: 'content-2',
              title: 'Next Content Item',
              content_item_number: 2
            }
          }
        });
      }, 500);
    });
  }

  getMockFullCourse(courseId: string): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          course: {
            _id: courseId,
            title: 'Full Course Sample',
            description: 'Complete course with all details',
            difficulty: 'intermediate',
            is_published: true
          },
          modules: [
            {
              _id: 'module-1',
              title: 'Module 1',
              module_number: 1,
              content_items: [
                {
                  _id: 'content-1',
                  title: 'Introduction',
                  content_type: 'video',
                  scenes: [
                    {
                      _id: 'scene-1',
                      scene_number: 1,
                      title: 'Welcome',
                      slide_content: 'Welcome to the course'
                    }
                  ]
                }
              ]
            }
          ],
          metadata: {
            total_modules: 1,
            total_content_items: 1,
            total_scenes: 1
          }
        });
      }, 500);
    });
  }

  updateMockCourseStage(courseId: string, stage: CourseStage): Promise<CourseStageUpdateResponse> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          success: true,
          description: 'Course stage updated successfully',
          data: {
            course_id: courseId,
            stage: stage,
            message: 'Course stage updated successfully',
            updated_at: new Date().toISOString()
          }
        });
      }, 500);
    });
  }

  getMockMinimalCourses(filters: MinimalCourseFilters): Promise<any> {
    return new Promise(resolve => {
      setTimeout(() => {
        // Transform mock courses to minimal format
        let courses = mockCoursesData.courses.map(c => ({
          _id: c.id,
          title: c.title,
          description: c.description,
          stage: c.status as CourseStage || 'draft',
          difficulty: c.difficulty,
          module_count: c.modules || 0,
          banner_image_url: c.banner,
          thumbnail_image_url: c.thumbnail,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          users_assigned: c.enrolledUsers || 0,
          rating: c.rating || 0,
          estimated_duration: {
            total: {
              hours: Math.floor((c.duration || 0) / 60),
              minutes: (c.duration || 0) % 60
            }
          }
        }));

        // Apply filters
        if (filters.stage) {
          courses = courses.filter(c => c.stage === filters.stage);
        }
        if (filters.difficulty) {
          courses = courses.filter(c => c.difficulty?.toLowerCase() === filters.difficulty?.toLowerCase());
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          courses = courses.filter(c => 
            c.title.toLowerCase().includes(searchLower) ||
            c.description.toLowerCase().includes(searchLower)
          );
        }

        // Apply sorting
        const sortBy = filters.sortBy || 'updatedAt';
        const sortOrder = filters.sortOrder || 'desc';
        courses.sort((a, b) => {
          let aVal = a[sortBy as keyof typeof a];
          let bVal = b[sortBy as keyof typeof b];
          if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });

        // Apply pagination
        const page = filters.page || 1;
        const limit = filters.limit || 21;
        const start = (page - 1) * limit;
        const paginatedCourses = courses.slice(start, start + limit);

        resolve({
          courses: paginatedCourses,
          pagination: {
            page,
            limit,
            total: courses.length,
            totalPages: Math.ceil(courses.length / limit),
            hasNext: page * limit < courses.length,
            hasPrev: page > 1
          },
          total: courses.length
        });
      }, 500);
    });
  }

  // New API methods for User Course Progress feature
  async getCourseDetailsForProgress(courseId: string): Promise<CourseDetailsResponse> {
    try {
      const response = await apiClient.get<{
        status: string;
        data: CourseDetailsResponse;
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.COURSES.GET_COURSE_DETAILS_PROGRESS(courseId));

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch course details');
    } catch (error) {
      console.error('Error fetching course details for progress:', error);
      throw error;
    }
  }

  async getModulesDetailed(courseId: string, userId?: string): Promise<ModulesDetailedResponse> {
    try {
      const params = userId ? { userId, includeContent: true } : { includeContent: true };
      const response = await apiClient.get<{
        status: string;
        data: ModulesDetailedResponse;
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.COURSES.GET_MODULES_DETAILED(courseId), { params });

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch modules detailed');
    } catch (error) {
      console.error('Error fetching modules detailed:', error);
      throw error;
    }
  }

  // Assessment APIs
  /**
   * @param languageCode pass it when the page is showing a translated course,
   *        so the questions read the same language as the rest of it. Omitted
   *        returns the original, exactly as before.
   */
  async getAssessmentAdmin(assessmentId: string, languageCode?: string) {
    try {
      const url = API_ENDPOINTS.COURSES.GET_ASSESSMENT_ADMIN(assessmentId);
      const response = await apiClient.get(
        languageCode ? `${url}?language=${encodeURIComponent(languageCode)}` : url
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching admin assessment:', error);
      throw error;
    }
  }

  /**
   * Create a manual PRE/POST assessment for a course / module / content item.
   * `assessment_level` is COURSE | MODULE | CONTENT_ITEM, `assessment_type` is
   * PRE | POST, `entity_id` is the course/module/content-item id.
   */
  async createAssessment(data: {
    assessment_level: 'COURSE' | 'MODULE' | 'CONTENT_ITEM';
    assessment_type: 'PRE' | 'POST';
    entity_id: string;
    title: string;
    description?: string;
  }) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ASSESSMENTS.CREATE, data);
      return response.data;
    } catch (error) {
      console.error('Error creating assessment:', error);
      throw error;
    }
  }

  /**
   * Soft-delete a whole assessment (pre/post) and detach it from its parent.
   */
  async deleteAssessment(assessmentId: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.ASSESSMENTS.DELETE(assessmentId));
      return response.data;
    } catch (error) {
      console.error('Error deleting assessment:', error);
      throw error;
    }
  }

  /**
   * Every assessment on a course, disabled ones included — drives the course
   * Completion Config tab.
   */
  async listCourseAssessments(courseId: string): Promise<CourseAssessmentConfigItem[]> {
    try {
      const response = await apiClient.get<ApiResponse<{ course_id: string; assessments: CourseAssessmentConfigItem[] }>>(
        API_ENDPOINTS.ASSESSMENTS.LIST_FOR_COURSE(courseId)
      );
      return response.data.data?.assessments || [];
    } catch (error) {
      console.error('Error fetching course assessments:', error);
      throw error;
    }
  }

  /**
   * What the current learner still has to pass before this course can complete.
   * Returns all_passed:true for courses with no completion requirements.
   */
  async getCourseCompletionRequirements(courseId: string): Promise<CourseCompletionRequirementStatus> {
    const response = await apiClient.get<ApiResponse<CourseCompletionRequirementStatus>>(
      API_ENDPOINTS.ASSESSMENTS.COMPLETION_REQUIREMENTS(courseId)
    );
    return (
      response.data.data || { course_id: courseId, requirements: [], all_passed: true, pending_count: 0 }
    );
  }

  /**
   * Mark whether a learner must pass this assessment for the course to be
   * considered complete. Does not affect whether the assessment is visible or
   * attemptable.
   */
  async setAssessmentCompletionRequirement(assessmentId: string, required_for_completion: boolean) {
    try {
      const response = await apiClient.patch(
        API_ENDPOINTS.ASSESSMENTS.SET_COMPLETION_REQUIREMENT(assessmentId),
        { required_for_completion }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating assessment completion requirement:', error);
      throw error;
    }
  }

  /* --------------------- Custom completion config -------------------- */

  /** Admin: full custom completion config for a course. */
  async getCompletionConfig(courseId: string): Promise<CourseCompletionConfig> {
    const response = await apiClient.get<ApiResponse<CourseCompletionConfig>>(
      API_ENDPOINTS.COMPLETION_CONFIG.GET(courseId)
    );
    return (
      response.data.data || {
        course_id: courseId,
        custom_enabled: false,
        manual_completion_enabled: true,
        conditions: [],
      }
    );
  }

  /** Admin: turn the learner-side manual lesson completion control on/off. */
  async setManualCompletionEnabled(
    courseId: string,
    manual_completion_enabled: boolean
  ): Promise<CourseCompletionConfig> {
    const response = await apiClient.patch<ApiResponse<CourseCompletionConfig>>(
      API_ENDPOINTS.COMPLETION_CONFIG.SET_MANUAL_COMPLETION_ENABLED(courseId),
      { manual_completion_enabled }
    );
    return response.data.data as CourseCompletionConfig;
  }

  /** Admin: turn the whole custom completion config on/off. */
  async setCompletionCustomEnabled(courseId: string, custom_enabled: boolean): Promise<CourseCompletionConfig> {
    const response = await apiClient.patch<ApiResponse<CourseCompletionConfig>>(
      API_ENDPOINTS.COMPLETION_CONFIG.SET_CUSTOM_ENABLED(courseId),
      { custom_enabled }
    );
    return response.data.data as CourseCompletionConfig;
  }

  /** Admin: create/update the Terms & Conditions condition inline. */
  async upsertTermsCondition(
    courseId: string,
    payload: { title?: string; source?: 'pdf' | 'inline'; html_content?: string; is_enabled?: boolean }
  ): Promise<CompletionCondition> {
    const response = await apiClient.put<ApiResponse<CompletionCondition>>(
      API_ENDPOINTS.COMPLETION_CONFIG.UPSERT_TERMS(courseId),
      payload
    );
    return response.data.data as CompletionCondition;
  }

  /** Admin: upload a T&C PDF document (multipart). */
  async uploadTermsDocument(courseId: string, file: File, title?: string): Promise<CompletionCondition> {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    const response = await apiClient.post<ApiResponse<CompletionCondition>>(
      API_ENDPOINTS.COMPLETION_CONFIG.UPLOAD_TERMS(courseId),
      form
    );
    return response.data.data as CompletionCondition;
  }

  /** Admin: enable/disable a condition. */
  async setConditionEnabled(courseId: string, conditionId: string, is_enabled: boolean): Promise<CompletionCondition> {
    const response = await apiClient.patch<ApiResponse<CompletionCondition>>(
      API_ENDPOINTS.COMPLETION_CONFIG.SET_CONDITION_ENABLED(courseId, conditionId),
      { is_enabled }
    );
    return response.data.data as CompletionCondition;
  }

  /** Admin: remove a condition. */
  async deleteCompletionCondition(courseId: string, conditionId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.COMPLETION_CONFIG.DELETE_CONDITION(courseId, conditionId));
  }

  /** Learner: aggregated completion gate (assessments + custom conditions). */
  async getCompletionGate(courseId: string): Promise<CourseCompletionGateStatus> {
    const response = await apiClient.get<ApiResponse<CourseCompletionGateStatus>>(
      API_ENDPOINTS.COMPLETION_CONFIG.GATE(courseId)
    );
    return (
      response.data.data || {
        course_id: courseId,
        all_satisfied: true,
        assessment_requirements: [],
        conditions: [],
        pending_count: 0,
      }
    );
  }

  /** Learner: accept a Terms & Conditions condition. */
  async acceptTerms(courseId: string, conditionId: string, version?: number): Promise<AcceptTermsResult> {
    const response = await apiClient.post<ApiResponse<AcceptTermsResult>>(
      API_ENDPOINTS.COMPLETION_CONFIG.ACCEPT_TERMS(courseId),
      { condition_id: conditionId, version, agreed: true }
    );
    return response.data.data as AcceptTermsResult;
  }

  /** Admin: a learner's T&C acceptance records for the progress view. */
  async getUserTermsAcceptances(courseId: string, userId: string): Promise<UserTermsAcceptanceStatus> {
    const response = await apiClient.get<ApiResponse<UserTermsAcceptanceStatus>>(
      API_ENDPOINTS.COMPLETION_CONFIG.USER_ACCEPTANCES(courseId, userId)
    );
    return (
      response.data.data || { course_id: courseId, user_id: userId, records: [] }
    );
  }

  async updateAssessmentSettings(assessmentId: string, settings: {
    passing_score?: number;
    time_limit_minutes?: number | null;
    max_attempts?: number | null;
    allow_retry?: boolean;
    shuffle_options?: boolean;
    show_feedback?: boolean;
    show_correct_answers?: boolean;
  }) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.ASSESSMENTS.UPDATE_SETTINGS(assessmentId), settings);
      return response.data;
    } catch (error) {
      console.error('Error updating assessment settings:', error);
      throw error;
    }
  }

  async addAssessmentQuestion(assessmentId: string, question: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ASSESSMENTS.ADD_QUESTION(assessmentId), question);
      return response.data;
    } catch (error) {
      console.error('Error adding assessment question:', error);
      throw error;
    }
  }

  async deleteAssessmentQuestion(assessmentId: string, questionId: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.ASSESSMENTS.DELETE_QUESTION(assessmentId, questionId));
      return response.data;
    } catch (error) {
      console.error('Error deleting assessment question:', error);
      throw error;
    }
  }

  async updateAssessmentQuestions(assessmentId: string, questions: any[]) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.ASSESSMENTS.UPDATE_QUESTIONS(assessmentId), { questions });
      return response.data;
    } catch (error) {
      console.error('Error updating assessment questions:', error);
      throw error;
    }
  }

  async getAssessmentLearner(assessmentId: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSES.GET_ASSESSMENT_LEARNER(assessmentId));
      return response.data;
    } catch (error) {
      console.error('Error fetching learner assessment:', error);
      throw error;
    }
  }

  /**
   * Enhance user input for course creation using AI
   * Returns enhanced input with suggestions and key concepts
   */
  async enhanceUserInput(params: {
    user_course_idea: string;
    targeted_audience?: string;
    difficulty_level?: string;
  }): Promise<{
    enhanced_input: string;
    suggestions: string[];
    key_concepts: string[];
  }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSES.ENHANCE_INPUT, params);
      return response.data.data;
    } catch (error) {
      console.error('Error enhancing user input:', error);
      throw error;
    }
  }

  /**
   * Update content item
   * Allows editing of title, HTML content, narration, description, duration, and media assets
   */
  async updateContentItem(
    contentItemId: string,
    data: {
      title?: string;
      html_content?: string;
      narration?: string;
      description?: string;
      estimated_time?: {
        hours: number;
        minutes: number;
      };
      media_assets?: {
        s3_audio_url?: string;
      };
    }
  ) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.COURSES.UPDATE_CONTENT_ITEM(contentItemId), data);

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update content item');
    } catch (error) {
      console.error('Error updating content item:', error);
      throw error;
    }
  }

  /**
   * Update scene
   * Allows editing of title, HTML content, and narration
   */
  async updateScene(
    sceneId: string,
    data: {
      title?: string;
      html_content?: string;
      narration?: string;
      scene_data?: Record<string, any>;
      media_urls?: {
        s3_audio_url?: string;
        s3_video_url?: string;
        thumbnail_url?: string;
      };
    }
  ) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.COURSES.UPDATE_SCENE(sceneId), data);

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update scene');
    } catch (error) {
      console.error('Error updating scene:', error);
      throw error;
    }
  }

  /**
   * Queue scene regeneration: saves updated fields and queues async processing.
   * The heavy work (TTS, captions, render) runs in the processor.
   * Poll getSceneStatus() for progress.
   */
  async regenerateSceneVideo(
    sceneId: string,
    updateData?: {
      title?: string;
      html_content?: string;
      narration?: string;
      scene_data?: Record<string, any>;
    }
  ) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.REGENERATE_SCENE_VIDEO(sceneId),
        updateData || {}
      );

      if (response.data.success) {
        // Async flow: backend returns queue confirmation, not video URLs.
        // Polling via getSceneStatus handles progress tracking.
        const outer_data = response.data.data;
        return outer_data || { success: true, message: response.data.description };
      }

      throw new Error(response.data.description || 'Failed to queue scene regeneration');
    } catch (error: any) {
      console.error('Error queuing scene regeneration:', error);
      throw error;
    }
  }

  /**
   * Get scene regeneration status (lightweight polling endpoint)
   */
  async getSceneStatus(sceneId: string): Promise<{
    is_regenerating: boolean;
    error_message: string;
    html_content?: string;
    media_urls?: {
      s3_video_url?: string;
      s3_audio_url?: string;
      thumbnail_url?: string;
    };
    duration?: number;
  }> {
    const response = await apiClient.get(API_ENDPOINTS.COURSES.SCENE_STATUS(sceneId));
    if (response.data.success && response.data.data?.data) {
      return response.data.data.data;
    }
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get scene status');
  }

  /**
   * Generate a scene's narration from its slide HTML using AI.
   * Uses the course's configured language; the backend caps the result at
   * 1500 characters. Pass the editor's current HTML to narrate unsaved edits.
   */
  async generateSceneNarration(
    sceneId: string,
    htmlContent?: string,
    options?: { language_code?: string }
  ): Promise<{
    narration: string;
    word_count?: number;
    language?: string;
    character_count?: number;
    max_characters?: number;
  }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.GENERATE_SCENE_NARRATION(sceneId),
        {
          ...(htmlContent ? { html_content: htmlContent } : {}),
          ...(options?.language_code ? { language_code: options.language_code } : {}),
        }
      );

      const data = response.data?.data || response.data;
      if (data?.narration) return data;

      throw new Error(response.data?.description || 'No narration returned from server');
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.response?.data?.message;
      throw new Error(serverMessage || error?.message || 'Failed to generate narration');
    }
  }

  /**
   * Generate narration text from HTML content using AI
   * Uses course language for narration generation
   */
  async generateNarration(
    contentItemId: string,
    htmlContent: string,
    options?: {
      language_code?: string;
    }
  ) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.GENERATE_NARRATION(contentItemId),
        {
          html_content: htmlContent,
          language_code: options?.language_code,
        }
      );

      console.log('Generate narration response (full):', response.data);
      console.log('Response.data.data:', response.data.data);
      console.log('Response.data.success:', response.data.success);

      // FIXED: Backend returns success=false even when data exists
      // Check for data.narration in the data object
      const responseData = response.data.data || response.data;

      // Check if narration exists in data
      if (responseData && responseData.narration) {
        console.log('✅ Narration found in response:', {
          narration: responseData.narration.substring(0, 100),
          wordCount: responseData.word_count,
          success: response.data.success
        });
        return responseData;
      }

      // If data exists but no narration field, log structure
      console.error('❌ Response has data but no narration field:', {
        hasData: !!response.data.data,
        dataKeys: response.data.data ? Object.keys(response.data.data) : [],
        responseKeys: Object.keys(response.data)
      });

      throw new Error(response.data.description || response.data.message || 'Failed to generate narration - no narration in response');
    } catch (error: any) {
      console.error('Narration generation error:', error);
      console.error('Error response:', error.response?.data);

      // CRITICAL FIX: Axios interceptor rejects when success=false
      // But the data might still be valid! Check error.response
      if (error.response && error.response.data && error.response.data.data) {
        const errorResponseData = error.response.data.data;

        // Check if narration exists in the error response
        if (errorResponseData.narration) {
          console.log('🔧 RECOVERED: Narration found in error.response.data.data:', {
            narration: errorResponseData.narration.substring(0, 100),
            wordCount: errorResponseData.word_count,
            recovered: true
          });

          // Return the data even though it came through error path
          return errorResponseData;
        }
      }

      // Re-throw with clear message only if no data recovered
      const errorMessage = error.response?.data?.description ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to generate narration';
      throw new Error(errorMessage);
    }
  }

  /**
   * Regenerate audio for content item from narration text
   * Uses TTS to generate audio, uploads to S3, and updates s3_audio_url
   */
  async regenerateContentItemAudio(
    contentItemId: string,
    options?: {
      language_code?: string;
      speed?: number;
    }
  ) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.REGENERATE_AUDIO(contentItemId),
        options || {}
      );

      console.log('Regenerate audio response:', response.data);

      // Handle nested data structure
      // Response: { success, data: { success, data: { s3_audio_url, narration } } }
      if (response.data.success && response.data.data) {
        const outerData = response.data.data;

        // Check for double nesting
        if (outerData.data && outerData.data.s3_audio_url) {
          console.log('Audio URL (double nested):', outerData.data.s3_audio_url);
          return outerData.data; // Return innermost data
        }

        // Single nesting
        if (outerData.s3_audio_url) {
          console.log('Audio URL (single nested):', outerData.s3_audio_url);
          return outerData;
        }
      }

      console.error('Audio response missing s3_audio_url:', response.data);
      throw new Error(response.data.description || 'Failed to regenerate audio - no URL returned');
    } catch (error: any) {
      console.error('Error regenerating audio:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  /**
   * Regenerate an HTML content item's html_content with AI.
   * Reuses the content-item generation agent on the backend — completes a broken/
   * incomplete lesson using the title, module context, optional feedback and the
   * current partial HTML. Returns the regenerated content item data.
   */
  async regenerateContentItemHtml(
    contentItemId: string,
    options?: {
      title?: string;
      feedback?: string;
      regenerate_images?: boolean;
    }
  ): Promise<{ started: boolean; _id: string }> {
    // Fire-and-forget: this kicks off the AI regeneration in the background and
    // returns immediately. Poll getContentItemHtmlStatus() until the status is
    // 'completed'/'failed', then read the fresh html_content.
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.REGENERATE_CONTENT(contentItemId),
        options || {}
      );
      if (response.data.success) {
        const outer = response.data.data;
        const inner = (outer && outer.data) ? outer.data : (outer || {});
        return { started: true, _id: inner._id || contentItemId };
      }
      throw new Error(response.data.description || 'Failed to start content regeneration');
    } catch (error: any) {
      console.error('Error regenerating content:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Attach an uploaded PDF file to an empty content item. The raw file is
   * already uploaded to S3 (storageService); this tells the backend to embed it
   * in a themed HTML page, store it and mark the item as an uploaded (read-only)
   * item. PDF only, max 50 MB. Returns the updated content item.
   */
  async uploadContentItemDocument(
    contentItemId: string,
    dto: { file_url: string; file_key?: string; file_type: string; file_name?: string; file_size?: number }
  ): Promise<{ _id: string; html_content?: string; media_assets?: any; content_source?: string; uploaded_file?: any }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.UPLOAD_CONTENT_DOCUMENT(contentItemId),
        dto
      );
      if (response.data.success && response.data.data) {
        const outer = response.data.data;
        const inner = (outer && outer.data) ? outer.data : outer;
        return inner;
      }
      throw new Error(response.data.description || 'Failed to upload document');
    } catch (error: any) {
      console.error('Error uploading content document:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Remove the uploaded file from a content item (keeps the item; resets it to
   * an empty themed starter so it can be edited or re-uploaded).
   */
  async removeContentItemUpload(contentItemId: string): Promise<void> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSES.REMOVE_CONTENT_DOCUMENT(contentItemId));
      if (response.data.success) return;
      throw new Error(response.data.description || 'Failed to remove uploaded file');
    } catch (error: any) {
      console.error('Error removing uploaded file:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Attach an uploaded MP4 directly to a scene-less video_presentation content
   * item. The raw file is already uploaded to S3 (storageService); this tells
   * the backend to set it as the item final video (no scene merge) and set the
   * item duration. MP4 only, max 90 MB. Returns the updated content item.
   */
  async uploadContentItemVideo(
    contentItemId: string,
    dto: { file_url: string; file_key?: string; file_type: string; file_name?: string; file_size?: number; duration_seconds?: number }
  ): Promise<{ _id: string; media_assets?: any; content_source?: string; uploaded_file?: any; estimated_time?: any }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.UPLOAD_CONTENT_VIDEO(contentItemId),
        dto
      );
      if (response.data.success && response.data.data) {
        const outer = response.data.data;
        const inner = (outer && outer.data) ? outer.data : outer;
        return inner;
      }
      throw new Error(response.data.description || 'Failed to upload video');
    } catch (error: any) {
      console.error('Error uploading content video:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Remove the uploaded video from a content item (keeps the item; restores the
   * scene tools so scenes can be added/merged instead).
   */
  async removeContentItemVideo(contentItemId: string): Promise<void> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSES.REMOVE_CONTENT_VIDEO(contentItemId));
      if (response.data.success) return;
      throw new Error(response.data.description || 'Failed to remove uploaded video');
    } catch (error: any) {
      console.error('Error removing uploaded video:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Poll the content-item AI HTML regeneration status. Returns the generation
   * status + the current html_content so the editor can reload once complete.
   */
  async getContentItemHtmlStatus(contentItemId: string): Promise<{
    status: string;
    error?: string;
    html_content?: string;
    media_assets?: { s3_html_url?: string; s3_audio_url?: string; s3_video_url?: string };
  }> {
    const response = await apiClient.get(API_ENDPOINTS.COURSES.CONTENT_ITEM_HTML_STATUS(contentItemId));
    const outer = response.data.data;
    const inner = (outer && outer.data) ? outer.data : (outer || {});
    return {
      status: inner.status || 'idle',
      error: inner.error,
      html_content: inner.html_content,
      media_assets: inner.media_assets,
    };
  }

  /**
   * AI regenerate/generate a scene's HTML slide content (scene equivalent of
   * regenerateContentItemHtml). Returns the updated scene html + media urls.
   */
  async regenerateSceneHtml(
    sceneId: string,
    options?: {
      title?: string;
      feedback?: string;
      regenerate_images?: boolean;
    }
  ): Promise<{ started: boolean; _id: string }> {
    // Fire-and-forget: kicks off the AI regeneration in the background and
    // returns immediately. Poll getSceneStatus() until is_regenerating is false,
    // then read the fresh html_content.
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.REGENERATE_SCENE_HTML(sceneId),
        options || {}
      );
      if (response.data.success) {
        const outer = response.data.data;
        const inner = (outer && outer.data) ? outer.data : (outer || {});
        return { started: true, _id: inner._id || sceneId };
      }
      throw new Error(response.data.description || 'Failed to start scene regeneration');
    } catch (error: any) {
      console.error('Error regenerating scene content:', error);
      const data = error?.response?.data;
      const serverMessage = data?.error?.error_description || data?.description || data?.error?.message || data?.message;
      if (serverMessage) throw new Error(serverMessage);
      throw error;
    }
  }

  /**
   * Queue video regeneration for content item (async via processor).
   * Poll getContentItemVideoStatus() for progress.
   */
  async regenerateContentItemVideo(contentItemId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.COURSES.REGENERATE_VIDEO(contentItemId)
      );

      if (response.data.success) {
        const inner = response.data.data?.data || response.data.data || response.data;
        return { success: true, message: inner.message || 'Queued for regeneration' };
      }

      throw new Error(response.data.description || 'Failed to queue video regeneration');
    } catch (error: any) {
      console.error('Error queuing video regeneration:', error);
      // Surface the server's real message (e.g. "No scenes with rendered videos
      // found…") so the caller can show it in a toast, not a generic error.
      const data = error?.response?.data;
      const serverMessage =
        data?.error?.error_description ||
        data?.description ||
        data?.error?.message ||
        data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      throw error;
    }
  }

  /**
   * Get content item video regeneration status (lightweight polling endpoint)
   */
  async getContentItemVideoStatus(contentItemId: string): Promise<{
    generation_status: {
      status: string;
      message?: string;
      error?: string;
      last_updated?: string;
    };
    media_assets?: {
      s3_video_url?: string;
    };
  }> {
    const response = await apiClient.get(
      API_ENDPOINTS.COURSES.CONTENT_ITEM_VIDEO_STATUS(contentItemId)
    );
    if (response.data.success && response.data.data?.data) {
      return response.data.data.data;
    }
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get content item video status');
  }
}

export const courseService = new CourseService();
